import React, { FC, useState, useEffect, useRef } from "react";
import QRCode from "react-qr-code";
import { colorsSelector } from "@redux/colorPalette/selectors";
import { resetPosition } from "@redux/discoveryPlaylist";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import {
	isCameraActivatedSelector,
	isIAPWebcamActivatedSelector,
	publicInteractionSelector,
} from "@redux/featureFlags/selector";
import { lightSelector } from "@redux/light/selectors";
import { setIsVideoRecording } from "@redux/session";
import {
	bamSessionClientCodeSelector,
	isVideoRecordingSelector,
} from "@redux/session/selectors";
import analytics from "lib/analytics";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLongPress } from "react-use";

import {
	takePicture,
	useStartOrStopRecording,
} from "components/b2b/Header/camera/camera.utils";
import FlashMessage from "components/features/FlashMessage/FlashMessage";
import { PATHS } from "constants/pathsB2b";
import { sendMultiScreenEventToTv } from "services/helpers/multiScreen";
import { useTvToIpadMultiScreenEventsListener } from "services/hooks/useMultiScreenEventListener";
import { PlayerEvent } from "shared-types/player-event";

interface Props {
	onLogoLongPress: () => void;
}

import {
	BamLogo,
	CameraIcon,
	HeaderIconGroup,
	HomeIcon,
	PhotoIcon,
	StyledHeader,
	HeaderIconButton,
	HighlightPoint,
	LanguageIcon,
	LightIcon,
	QRCodeContainer,
	ModalContent,
} from "./Header.style";
import LanguagesPopover from "./LanguagesPopover";
import { LogoButton } from "../Logo/Logo.style";
import { userUuidSelector, withBamLayoutSelector } from "@redux/user/selectors";
import { StyledTslLogo } from "./Header.style";
import { roomIdSelector } from "@redux/room/selectors";
import { Button, IconButton, Modal } from "@mui/material";

const PHOTO_DURATION = 6500;

export const Header: FC<Props> = ({ onLogoLongPress }) => {
	const qrCodeRef = useRef(null);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const intl = useIntl();
	const initFlashMessage = {
		message: "",
		isShown: false,
	};
	const [isPhotoActive, setIsPhotoActive] = useState<boolean>(false);
	const [photoTimeOutId, setPhotoTimeOutId] = useState<NodeJS.Timeout | null>(
		null,
	);
	const [flashMessage, setFlashMessage] = useState<{
		message: string;
		isShown: boolean;
	}>(initFlashMessage);
	const [isQRCodeModalOpen, setisQRCodeModalOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const colors = useSelector(colorsSelector);
	const startOrStopRecording = useStartOrStopRecording();
	const longPressProps = useLongPress(onLogoLongPress);
	const isVideoRecording = useSelector(isVideoRecordingSelector);
	const clientCode = useSelector(bamSessionClientCodeSelector);
	const lights = useSelector(lightSelector);
	const isCameraActivated = useSelector(isCameraActivatedSelector);
	const isIAPWebcamActivated = useSelector(isIAPWebcamActivatedSelector);
	const withBamLayout = useSelector(withBamLayoutSelector);
	const publicInteraction = useSelector(publicInteractionSelector);
	const roomId = useSelector(roomIdSelector);
	const userUuid = useSelector(userUuidSelector);
	// For TVC video launch
	const [showLaunchVideo, setShowLaunchVideo] = useState(
		userUuid === "43ef9df9-ae92-4cc1-aa09-94ac9d43ae76",
	);

	const onTouchPhoto = () => {
		if (clientCode) {
			isIAPWebcamActivated
				? sendMultiScreenEventToTv(PlayerEvent.TAKE_PHOTO, clientCode)
				: takePicture(clientCode);
			setFlashMessage({
				message: intl.formatMessage({ id: "flash-messages.photo" }),
				isShown: true,
			});
			setIsPhotoActive(true);
			const photoTimeOut = setTimeout(() => {
				setIsPhotoActive(false);
			}, PHOTO_DURATION);
			setPhotoTimeOutId(photoTimeOut);
			analytics.track("header_photo");
		}
	};

	const onTouchVideo = () => {
		if (!isVideoRecording) {
			setFlashMessage({
				message: intl.formatMessage({ id: "flash-messages.video" }),
				isShown: true,
			});
		}

		if (isIAPWebcamActivated) {
			sendMultiScreenEventToTv(
				isVideoRecording ? PlayerEvent.STOP_VIDEO : PlayerEvent.TAKE_VIDEO,
				clientCode,
			);
		} else {
			startOrStopRecording();
		}

		analytics.track("header_video");
	};

	useTvToIpadMultiScreenEventsListener({
		[PlayerEvent.CAMERA_ON]: () => {
			dispatch(setIsVideoRecording(true));
		},
		[PlayerEvent.CAMERA_OFF]: () => {
			dispatch(setIsVideoRecording(false));
		},
	});

	const clickHome = () => {
		dispatch(resetPosition());
		navigate(PATHS.HOME, { state: { resetState: true } });
	};

	const openPopover = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		setAnchorEl(e.currentTarget);
		analytics.track("header_language");
	};

	const closeMenu = () => {
		setAnchorEl(null);
	};

	const onTouchLight = () => {
		navigate(PATHS.LIGHTS, { state: { resetState: true } });
		analytics.track("header_lights");
	};

	const trackLogoTouch = () => analytics.track("header_logo");

	const onMessageClose = () => setFlashMessage(initFlashMessage);

	const toggleQrCodeModal = () => setisQRCodeModalOpen(!isQRCodeModalOpen);
	const qrCodeUrl = `${window.location.origin}/catalog/${roomId}`;

	const downloadQRCode = () => {
		if (!qrCodeRef.current) return;
		const svgData = new XMLSerializer().serializeToString(qrCodeRef.current);
		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");
		const img = new Image();
		img.onload = () => {
			canvas.width = img.width;
			canvas.height = img.height;
			if (ctx) {
				ctx.drawImage(img, 0, 0);
			}
			const pngFile = canvas.toDataURL("image/png");
			const downloadLink = document.createElement("a");
			downloadLink.download = "QRCode";
			downloadLink.href = `${pngFile}`;
			downloadLink.click();
		};
		img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
	};

	const handleLaunchVideo = () => {
		sendMultiScreenEventToTv(
			PlayerEvent.PLAY_CUSTOMER_VIDEO,
			`${process.env.REACT_APP_ASSETS_BASE_URL}/customers/videos/NIKKOS_KARAOKE.mp4`,
		);
		setShowLaunchVideo(false);
	};

	useEffect(() => {
		if (!isPhotoActive && photoTimeOutId !== null) {
			clearTimeout(photoTimeOutId);
		}
		() => {
			if (isVideoRecording) {
				startOrStopRecording();
			}
		};
	}, [isPhotoActive, photoTimeOutId]);

	return (
		<StyledHeader {...{ colors }}>
			<HeaderIconGroup>
				<HeaderIconButton color="inherit" onClick={clickHome}>
					<HomeIcon {...{ colors }} />
				</HeaderIconButton>

				<HeaderIconButton color="inherit" onClick={openPopover}>
					<LanguageIcon {...{ colors }} />
				</HeaderIconButton>
				{publicInteraction.isActivated && (
					<HeaderIconButton>
						<QRCodeContainer>
							<QRCode value={qrCodeUrl} size={40} onClick={toggleQrCodeModal} />
						</QRCodeContainer>
					</HeaderIconButton>
				)}
				{showLaunchVideo && (
					<HeaderIconButton>
						<Button onClick={handleLaunchVideo}>Launch video</Button>
					</HeaderIconButton>
				)}
			</HeaderIconGroup>

			<LogoButton {...longPressProps} onClick={trackLogoTouch}>
				{withBamLayout ? <BamLogo /> : <StyledTslLogo />}
			</LogoButton>

			<HeaderIconGroup>
				{lights.length > 0 && (
					<HeaderIconButton color="inherit" onClick={onTouchLight}>
						<LightIcon {...{ colors }} />
					</HeaderIconButton>
				)}

				{isCameraActivated && clientCode && (
					<>
						<HeaderIconButton color="inherit" onClick={onTouchPhoto}>
							<PhotoIcon {...{ colors }} />
							{isPhotoActive && <HighlightPoint {...{ colors }} />}
						</HeaderIconButton>
						<HeaderIconButton color="inherit" onClick={onTouchVideo}>
							<CameraIcon {...{ colors }} />
							{isVideoRecording && <HighlightPoint {...{ colors }} />}
						</HeaderIconButton>
					</>
				)}
			</HeaderIconGroup>
			<FlashMessage
				message={flashMessage.message}
				isShown={flashMessage.isShown}
				onClose={onMessageClose}
			/>
			<LanguagesPopover {...{ anchorEl, closeMenu }} />
			<Modal
				open={isQRCodeModalOpen}
				onClose={toggleQrCodeModal}
				sx={ModalContent}
			>
				<>
					<IconButton
						color="inherit"
						aria-label="download"
						onClick={downloadQRCode}
						size="large"
					>
						<DownloadForOfflineIcon />
					</IconButton>

					<QRCode
						value={qrCodeUrl}
						size={500}
						onClick={toggleQrCodeModal}
						ref={qrCodeRef}
					/>
				</>
			</Modal>
		</StyledHeader>
	);
};
